import type { ReactNode } from 'react';
import type { FieldErrors } from '@/shared/api/submit';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import type { SelectOption } from '@/shared/ui/selectTypes';
import type { ProfileValues } from './profileForm';

/** Controlled form state shared by the create / edit pages and their field groups. */
export interface ProfileFormApi {
  values: ProfileValues;
  errors: FieldErrors;
  /** Merge a change into the values and drop the listed (server-side) error keys. */
  patch: (change: Partial<ProfileValues>, ...touchedErrors: string[]) => void;
}

type KeysOf<T> = { [K in keyof ProfileValues]: ProfileValues[K] extends T ? K : never }[keyof ProfileValues];

const INPUT_STYLE = { maxWidth: 500 };

type TextFieldProps = {
  form: ProfileFormApi;
  field: KeysOf<string>;
  /** Server error key when it differs from `field`. */
  errorKey: string;
  label: ReactNode;
  type?: 'text' | 'password' | 'tel' | 'number';
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  hint?: ReactNode;
  /** Input filter: returns the value to store, or `null` to reject the keystroke. */
  onValueChange?: (value: string) => string | null;
};

export const TextField = ({
  form,
  field,
  errorKey,
  label,
  type = 'text',
  placeholder,
  maxLength,
  disabled,
  hint,
  onValueChange,
}: TextFieldProps) => (
  <FormField label={label} errors={form.errors[errorKey]}>
    <input
      type={type}
      className="form-control input-sm"
      style={INPUT_STYLE}
      value={form.values[field]}
      placeholder={placeholder}
      maxLength={maxLength}
      disabled={disabled}
      autoComplete={type === 'password' ? 'new-password' : undefined}
      onChange={(e) => {
        const next = onValueChange ? onValueChange(e.target.value) : e.target.value;
        if (next !== null) form.patch({ [field]: next }, errorKey);
      }}
    />
    {hint}
  </FormField>
);

type CheckFieldProps = {
  form: ProfileFormApi;
  field: KeysOf<boolean>;
  errorKey: string;
  label: string;
  disabled?: boolean;
  hint?: ReactNode;
};

export const CheckField = ({ form, field, errorKey, label, disabled, hint }: CheckFieldProps) => (
  <FormField label={label} errors={form.errors[errorKey]}>
    <div>
      <input
        type="checkbox"
        checked={form.values[field]}
        disabled={disabled}
        onChange={(e) => form.patch({ [field]: e.target.checked }, errorKey)}
      />{' '}
      {hint}
    </div>
  </FormField>
);

type SelectFieldProps = {
  form: ProfileFormApi;
  field: KeysOf<string>;
  errorKey: string;
  label: string;
  options: SelectOption[];
  isLoading?: boolean;
  disabled?: boolean;
  clearable?: boolean;
};

export const SelectField = ({
  form,
  field,
  errorKey,
  label,
  options,
  isLoading,
  disabled,
  clearable = false,
}: SelectFieldProps) => (
  <FormField label={label} errors={form.errors[errorKey]}>
    <FilterSelect
      options={options}
      value={form.values[field]}
      placeholder={label}
      width={300}
      isLoading={isLoading}
      isDisabled={disabled}
      isClearable={clearable}
      onChange={(value) => form.patch({ [field]: value }, errorKey)}
    />
  </FormField>
);

/** Non-field (`server` / `common`) errors returned on save. */
export const FormErrorSummary = ({ errors }: { errors: FieldErrors }) => (
  <>
    {[...(errors.server ?? []), ...(errors.common ?? [])].map((message) => (
      <p key={message} className="text-red">
        {message}
      </p>
    ))}
  </>
);
